import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import { errorMessage } from "@/lib/api";
import type { Project } from "./api";

/** 確認のために打ってもらう言葉。 */
const CONFIRM_WORD = "削除";

/**
 * 選んだ案件をまとめて削除する確認ダイアログ。
 *
 * **案件は現場の記録を抱えている**（明細・作業実績・取り込んだ発注書）。消える
 * 範囲が件数では読めないので、共通の [[ConfirmDialog]]（文章 1 つ）ではなく
 * 専用のダイアログにしてある（見積の [[QuotationBulkDeleteDialog]] と同じ作り）。
 * ここで見せるのは 3 つ:
 *
 * - **何が消えるか** … 案件ごとの明細行数を一覧で出す。「合計 N 件」だけだと、
 *   明細の入った案件が混ざっていても気づけない
 * - **何が残るか** … 図面 PDF と見積は消えない（案件から外れるだけ）。消えたと
 *   思って上げ直されるのを防ぐ
 * - **止まる条件** … 出荷済みの案件が 1 件でも混ざると、まとめて中止する
 *
 * そのうえで**「削除」と打ってもらう**。押し間違いで消えると、明細も作業実績も
 * 入れ直すことになり取り返しがつかない。
 */
export default function ProjectBulkDeleteDialog({
  isOpen,
  projects,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  /** 画面で選ばれている案件。 */
  projects: Project[];
  onClose: () => void;
  /** 削除を実行する。失敗したらそのまま投げ返すと、理由をここに出す。 */
  onConfirm: () => Promise<void>;
}) {
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 開くたびに入力と前回の失敗理由を捨てる。持ち越すと、打ち直さずに
  // 削除できてしまう。
  useEffect(() => {
    if (!isOpen) return;
    setWord("");
    setError(null);
  }, [isOpen]);

  const itemCount = projects.reduce((sum, p) => sum + p.item_count, 0);
  // 出荷済みの案件は消せない。選択から外す相手を先に教える。
  const shipped = projects.filter((p) => p.shipped_at != null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(errorMessage(e, "削除に失敗しました。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="2xl">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2 text-danger">
          <AlertTriangle size={20} />
          選んだ案件の削除
        </ModalHeader>
        <ModalBody>
          <p className="text-sm">
            案件 <span className="font-bold">{projects.length} 件</span>を、明細{" "}
            <span className="font-bold">{itemCount} 行</span>ごと削除します。
            <br />
            <span className="font-bold text-danger">この操作は取り消せません。</span>
          </p>

          <ul className="list-disc space-y-0.5 rounded-lg bg-default-50 px-6 py-3 text-sm text-default-600">
            <li>
              {
                "明細と、明細に入力した作業実績（工数）、取り込んだ発注書も一緒に消えます。"
              }
            </li>
            {/* 日本語の文を JSX のテキストのまま折り返すと、改行が半角空白に
                なって文の途中に空白が入る。文字列式で持つ。 */}
            <li>
              {
                "図面 PDF は消えませんが、この案件から外れます（どの案件にも属さない図面になります）。"
              }
            </li>
            <li>
              {
                "見積は消えません。受注のときに張った明細の紐付けだけが無くなるので、見積は受注済みのまま残ります。"
              }
            </li>
            <li>
              出荷済みの案件は削除できません。1 件でも混ざっていると、
              <span className="font-bold">まとめて中止</span>します。
            </li>
          </ul>

          {shipped.length > 0 && (
            <p className="text-sm text-warning-600">
              {`出荷済みの案件が ${shipped.length} 件あります（${shipped
                .map((p) => p.project_number)
                .join("、")}）。`}
              {"選択から外してください。"}
            </p>
          )}

          <div className="max-h-56 overflow-auto rounded-lg border border-default-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-default-50 text-left text-tiny text-default-600">
                <tr className="h-8">
                  <th className="px-3 font-bold">工番</th>
                  <th className="px-3 font-bold">案件名</th>
                  <th className="px-3 font-bold">得意先</th>
                  <th className="px-3 font-bold">出荷</th>
                  <th className="px-3 text-right font-bold">明細</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr
                    key={project.project_id}
                    className="h-9 border-t border-default-100"
                  >
                    <td className="px-3 font-medium">{project.project_number}</td>
                    <td className="truncate px-3">{project.project_title ?? "—"}</td>
                    <td className="truncate px-3">{project.client_name ?? "—"}</td>
                    <td className="px-3">
                      <Chip
                        size="sm"
                        variant="flat"
                        color={project.shipped_at ? "success" : "default"}
                      >
                        {project.shipped_at ? "出荷済" : "未出荷"}
                      </Chip>
                    </td>
                    <td className="px-3 text-right">{project.item_count} 行</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Input
            label={`確認のため「${CONFIRM_WORD}」と入力してください`}
            labelPlacement="outside"
            variant="bordered"
            radius="sm"
            value={word}
            onValueChange={setWord}
            data-testid="project-bulk-delete-word"
            aria-label={`確認のため ${CONFIRM_WORD} と入力`}
          />

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="danger"
            radius="full"
            data-testid="confirm"
            isDisabled={word.trim() !== CONFIRM_WORD || projects.length === 0}
            isLoading={busy}
            onPress={() => void confirm()}
          >
            {projects.length} 件を削除
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
